import { useState } from "react";
import {
  Alert, Box, Button, Checkbox, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  otConsumptionsApi, surgicalKitsApi,
  type CreateOtConsumptionLineDto, type CreateOtImplantDto, type ItemDto,
} from "@/api/endpoints";
import { SurgicalKitPicker } from "@/components/SurgicalKitPicker";
import { ItemPicker } from "@/components/Pickers";
import { BatchSelect } from "@/components/BatchSelect";
import { WarehousePicker } from "@/components/WarehousePicker";
import { errorMessage } from "@/api/client";
import { PatientSummaryByUhid } from "@/components/PatientSummary";
import { PatientUhidField } from "@/components/PatientUhidField";
import {
  LasaConfirmDialog, lasaConflictsFrom, type ServerLasaConflict,
} from "@/components/LasaConfirmDialog";
import { BinHint } from "@/components/BinHint";

/** A consumed line. `item` is local UI state; only its id goes to the API. */
interface DraftLine {
  key: string;
  item: ItemDto | null;
  batchId: string;
  qty: number;
  isImplant: boolean;
}

/** An implant row — same shape plus the UDI, which is mandatory. */
interface DraftImplant {
  key: string;
  item: ItemDto | null;
  batchId: string;
  udi: string;
}

const blankLine = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, batchId: "", qty: 1, isImplant: false,
});
const blankImplant = (): DraftImplant => ({
  key: crypto.randomUUID(), item: null, batchId: "", udi: "",
});

/** UC-FN-HOS-02 — Create OT consumption with implants (UDI mandatory per row). */
export function CreateOtConsumptionPage() {
  const navigate = useNavigate();
  const [uhid, setUhid] = useState("");
  const [lookupUhid, setLookupUhid] = useState(""); // committed UHID → patient preview
  const [admissionId, setAdmissionId] = useState("");
  const [procedureCode, setProcedureCode] = useState("");
  const [otWarehouseId, setOtWarehouseId] = useState("");
  const [surgicalKitId, setSurgicalKitId] = useState("");
  const [surgeon, setSurgeon] = useState("");
  const [procedureAt, setProcedureAt] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);
  const [implants, setImplants] = useState<DraftImplant[]>([]);

  const usableLines = lines.filter(l => l.item && l.batchId && l.qty > 0);
  const usableImplants = implants.filter(i => i.item && i.batchId && i.udi);

  // Same confusable gate as the pharmacy counter — theatre stock is administered directly.
  const [lasaConflicts, setLasaConflicts] = useState<ServerLasaConflict[] | null>(null);

  const mutation = useMutation({
    meta: { successMessage: "OT consumption created" },
    mutationFn: (lasaAcknowledgedItemIds?: string[]) => otConsumptionsApi.create({
      uhid, admissionId: admissionId || undefined, procedureCode, otWarehouseId,
      surgicalKitId: surgicalKitId || undefined, surgeon: surgeon || undefined,
      procedureAt: procedureAt || undefined,
      lines: usableLines.map((l): CreateOtConsumptionLineDto => ({
        itemId: l.item!.id, batchId: l.batchId, qty: l.qty, isImplant: l.isImplant,
      })),
      implants: usableImplants.map((i): CreateOtImplantDto => ({
        itemId: i.item!.id, batchId: i.batchId, udi: i.udi,
      })),
      lasaAcknowledgedItemIds,
    }),
    onSuccess: () => navigate("/hospital/ot"),
    onError: (err) => setLasaConflicts(lasaConflictsFrom(err)),
  });

  /**
   * Load a kit's contents onto the form. This is the whole point of a kit — it is a template, and
   * without expansion picking one only stores an id and leaves the nurse keying every line by hand.
   *
   * Consumable lines land in "items consumed"; implant lines land in both, because an implant is
   * consumed *and* needs a UDI recorded per unit. Batches stay empty for the operator to pick, since
   * a kit says what to issue, not which batch is on the shelf today.
   */
  const [kitError, setKitError] = useState<string | null>(null);
  const applyKit = async (kitId: string) => {
    setKitError(null);
    try {
      const kit = await surgicalKitsApi.get(kitId);
      if (kit.items.length === 0) {
        setKitError(`${kit.code} has no items yet — add them under Hospital → Surgical kits.`);
        return;
      }
      setLines(kit.items.map(i => ({
        key: crypto.randomUUID(),
        item: { id: i.itemId, sku: i.itemSku, name: i.itemName } as ItemDto,
        batchId: "", qty: i.qty, isImplant: i.isImplant,
      })));
      setImplants(kit.items.filter(i => i.isImplant).map(i => ({
        key: crypto.randomUUID(),
        item: { id: i.itemId, sku: i.itemSku, name: i.itemName } as ItemDto,
        batchId: "", udi: "",
      })));
    } catch (e) {
      setKitError(errorMessage(e));
    }
  };

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setLines(prev => prev.map(l => l.key === key ? { ...l, ...patch } : l));
  const updateImplant = (key: string, patch: Partial<DraftImplant>) =>
    setImplants(prev => prev.map(l => l.key === key ? { ...l, ...patch } : l));

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New OT consumption</Typography>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <PatientUhidField value={uhid} required sx={{ flex: 1 }}
              onChange={setUhid} onCommit={setLookupUhid} />
            <TextField label="Admission ID" value={admissionId} size="small" sx={{ flex: 1 }}
              onChange={(e) => setAdmissionId(e.target.value)} />
            <TextField label="Procedure code" value={procedureCode} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setProcedureCode(e.target.value)} />
          </Stack>
          {lookupUhid && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <PatientSummaryByUhid dense uhid={lookupUhid} />
            </Paper>
          )}
          <Stack direction="row" spacing={2}>
            <WarehousePicker label="OT warehouse" value={otWarehouseId} required sx={{ flex: 1 }}
              // Batches are per warehouse, so changing it invalidates every line's batch.
              onChange={(v) => {
                setOtWarehouseId(v);
                setLines(prev => prev.map(l => ({ ...l, batchId: "" })));
                setImplants(prev => prev.map(i => ({ ...i, batchId: "" })));
              }} />
            <SurgicalKitPicker value={surgicalKitId} sx={{ flex: 1 }}
              helperText="Picking a kit loads its items as lines"
              onChange={(v) => { setSurgicalKitId(v); if (v) applyKit(v); }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Surgeon" value={surgeon} size="small" sx={{ flex: 1 }}
              onChange={(e) => setSurgeon(e.target.value)} />
            <TextField label="Procedure at" type="datetime-local" value={procedureAt} size="small"
              InputLabelProps={{ shrink: true }} sx={{ flex: 1 }}
              onChange={(e) => setProcedureAt(e.target.value)} />
          </Stack>

          {kitError && <Alert severity="warning" onClose={() => setKitError(null)}>{kitError}</Alert>}
          {!otWarehouseId && (
            <Alert severity="info">Pick an OT warehouse first — batches and bins are per warehouse.</Alert>
          )}

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Items consumed</Typography>
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 260 }}>Item *</TableCell>
                <TableCell sx={{ width: 240 }}>Batch · expiry *</TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Qty *</TableCell>
                <TableCell width={90}>Implant?</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={l.key}>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <ItemPicker value={l.item} label={`Line ${i + 1}`} required error={!l.item}
                        // Changing the item invalidates the batch — it belonged to the old one.
                        onChange={(v) => updateLine(l.key, { item: v, batchId: "" })} />
                      <BinHint itemId={l.item?.id} warehouseId={otWarehouseId} label="Pick from" />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <BatchSelect
                      itemId={l.item?.id} warehouseId={otWarehouseId}
                      value={l.batchId} qty={l.qty}
                      onChange={(batchId) => updateLine(l.key, { batchId })}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField size="small" type="number" sx={{ width: 80 }} value={l.qty}
                      inputProps={{ min: 0, step: "any", style: { textAlign: "right" } }}
                      onChange={(e) => updateLine(l.key, { qty: Number(e.target.value) || 0 })} />
                  </TableCell>
                  <TableCell><Checkbox checked={l.isImplant}
                    onChange={(e) => updateLine(l.key, { isImplant: e.target.checked })} /></TableCell>
                  <TableCell>
                    <IconButton size="small" disabled={lines.length === 1}
                      onClick={() => setLines(prev => prev.filter(x => x.key !== l.key))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(prev => [...prev, blankLine()])}>
            Add item
          </Button>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Implant register (UDI)</Typography>
          <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 260 }}>Item *</TableCell>
                <TableCell sx={{ width: 240 }}>Batch · expiry *</TableCell>
                <TableCell>UDI *</TableCell><TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {implants.map((u, i) => (
                <TableRow key={u.key}>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <ItemPicker value={u.item} label={`Implant ${i + 1}`} required error={!u.item}
                        onChange={(v) => updateImplant(u.key, { item: v, batchId: "" })} />
                      <BinHint itemId={u.item?.id} warehouseId={otWarehouseId} label="Pick from" />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <BatchSelect
                      itemId={u.item?.id} warehouseId={otWarehouseId}
                      value={u.batchId} qty={1}
                      onChange={(batchId) => updateImplant(u.key, { batchId })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" fullWidth value={u.udi} error={!u.udi}
                      placeholder="Unique device identifier"
                      onChange={(e) => updateImplant(u.key, { udi: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() =>
                      setImplants(prev => prev.filter(x => x.key !== u.key))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setImplants(prev => [...prev, blankImplant()])}>
            Add implant
          </Button>

          {/* The LASA block is shown by its own dialog, not as a red banner behind it. */}
          {mutation.isError && !lasaConflicts && (
            <Alert severity="error">{errorMessage(mutation.error)}</Alert>
          )}
          {/* An implant row started but left without a UDI would be silently dropped from the
              payload, so say so rather than saving a record that quietly omits an implant. */}
          {implants.length > usableImplants.length && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {implants.length - usableImplants.length} implant row(s) are incomplete — each needs an
              item, a batch and a UDI, or it will not be recorded.
            </Alert>
          )}
          {(!uhid || !procedureCode || !otWarehouseId || usableLines.length === 0) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {!uhid ? "UHID is required."
                : !procedureCode ? "Procedure code is required."
                : !otWarehouseId ? "Pick an OT warehouse."
                : "Add at least one line with an item, batch and quantity."}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => navigate("/hospital/ot")}>Cancel</Button>
            <Button variant="contained" onClick={() => mutation.mutate(undefined)}
              disabled={!uhid || !procedureCode || !otWarehouseId ||
                usableLines.length === 0 || mutation.isPending}>
              Save draft
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <LasaConfirmDialog
        conflicts={lasaConflicts}
        busy={mutation.isPending}
        onCancel={() => setLasaConflicts(null)}
        onAccept={(itemIds) => { setLasaConflicts(null); mutation.mutate(itemIds); }}
      />
    </Box>
  );
}
